/*
 * Lawn Tips cart drawer extras (replaces the Monster Cart app).
 * Adds: order-history "bought together" upsells (with Shopify recommendations
 * fallback), auto-ticked shipping protection (opt-out remembered), free gift
 * at a spend threshold, coupon code box, free-shipping confetti, and
 * lightweight analytics beacons.
 *
 * Config comes from #CartDrawer-Config, rendered by snippets/cart-drawer.liquid
 * from Theme settings > "Cart drawer (Lawn Tips)".
 *
 * All handlers use event delegation on `document` because the drawer's
 * inner HTML is replaced by the Section Rendering API on every cart change.
 */
(() => {
  'use strict';

  const TRACK_URL = 'https://lawntips-ask-ai.bensims.workers.dev/cart-event';
  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  const PROTECT_OPTOUT_KEY = 'lt_protect_off';

  /* ------------------------------ analytics ------------------------------ */

  const sid = (() => {
    try {
      let s = sessionStorage.getItem('lt_cart_sid');
      if (!s) {
        s = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('lt_cart_sid', s);
      }
      return s;
    } catch (e) {
      return 'anon';
    }
  })();

  function track(type, data) {
    try {
      const payload = JSON.stringify(Object.assign({ type: type, sid: sid, page: location.pathname }, data || {}));
      if (navigator.sendBeacon) {
        navigator.sendBeacon(TRACK_URL, new Blob([payload], { type: 'text/plain' }));
      } else {
        fetch(TRACK_URL, { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'text/plain' } });
      }
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  /* ----------------------------- DOM helpers ----------------------------- */

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function config() {
    const el = document.getElementById('CartDrawer-Config');
    return el ? el.dataset : null;
  }

  function cartTotals() {
    // Prefer the ship bar; fall back to the config div (wholesale has no bar).
    const bar = document.querySelector('#CartDrawer .lt-shipbar');
    const ds = bar ? bar.dataset : config() || {};
    return {
      cents: parseInt(ds.total, 10) || 0,
      count: parseInt(ds.count, 10) || 0,
    };
  }

  function realItemCount() {
    return document.querySelectorAll('#CartDrawer .cart-item').length;
  }

  function moneyToCents(txt) {
    // First money-looking number only, so "$95.00 $76.00" can never merge into garbage.
    const m = String(txt || '').replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : 0;
  }

  function fmtMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ----------------------- wholesale total (WPD) ------------------------- */

  // For WPD wholesale customers the total is shown by summing the wholesale line
  // prices the app has already painted onto each item (self-consistent with what
  // they see), plus protection. Regular customers are untouched — they keep the
  // Liquid-rendered cart.total_price. Runs after WPD updates the line prices.
  function syncWholesaleTotal() {
    const cfg = config();
    if (!cfg || cfg.wholesale !== '1') return;
    if (!document.querySelector('cart-drawer')) return;
    // Per-line prices WPD maintains on each item. For wholesale customers WPD
    // rewrites each span to "<s>$retail</s>$wholesale" — strip the struck-out
    // retail part and parse what's left (the wholesale price).
    const spans = [].slice
      .call(document.querySelectorAll('#CartDrawer [data-wpd-cart-drawer-line-price]'))
      .filter((el) => !el.closest('s'));
    if (!spans.length) return;
    let cents = 0;
    spans.forEach((s) => {
      const clone = s.cloneNode(true);
      clone.querySelectorAll('s, del, [style*="line-through"]').forEach((n) => n.remove());
      cents += moneyToCents(clone.textContent);
    });
    if (cents <= 0) return;
    // NOTE: we deliberately do NOT subtract rewards / discount codes here.
    // Trade customers can't stack a rewards code on top of wholesale pricing at
    // checkout, so showing a discounted total in the drawer would under-quote
    // what they actually pay. The coupon box is hidden for wholesale (below), so
    // the wholesale total = wholesale line prices + protection, matching checkout.
    // Protection is a flat add-on (not wholesale-priced).
    if (cfg.protectKey && cfg.protectCents) cents += parseInt(cfg.protectCents, 10) || 0;
    // CRITICAL: only write when the value actually changed. Our writes re-fire
    // the drawer's MutationObserver (and WPD's own observers) — unconditional
    // writes create an infinite write→observe→write loop that freezes the tab
    // for wholesale customers.
    const estText = fmtMoney(cents) + ' AUD';
    const btnText = 'CHECKOUT • ' + fmtMoney(cents);
    const est = document.querySelector('#CartDrawer .totals__total-value');
    if (est && est.textContent !== estText) est.textContent = estText;
    const btn = document.getElementById('CartDrawer-Checkout');
    if (btn && btn.textContent.trim().replace(/\s+/g, ' ') !== btnText) btn.textContent = btnText;
  }

  /* --------------------------- cart mutations ---------------------------- */

  // Swap in freshly rendered sections without opening the drawer (used for
  // background syncs like auto-protection, so the drawer doesn't pop open).
  function applySections(data) {
    if (!data.sections) return;
    const parse = (html, sel) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(sel);
    };
    const drawerSrc = data.sections['cart-drawer'] && parse(data.sections['cart-drawer'], '#CartDrawer');
    const target = document.getElementById('CartDrawer');
    if (drawerSrc && target) {
      target.innerHTML = drawerSrc.innerHTML;
      const overlay = document.getElementById('CartDrawer-Overlay');
      const d = drawerEl();
      if (overlay && d && typeof d.close === 'function') overlay.addEventListener('click', () => d.close());
    }
    const bubbleSrc = data.sections['cart-icon-bubble'] && parse(data.sections['cart-icon-bubble'], '.shopify-section');
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleSrc && bubble) bubble.innerHTML = bubbleSrc.innerHTML;
  }

  // POST to a cart endpoint and re-render the drawer + header bubble.
  // opts.silent: update the DOM without opening the drawer.
  async function cartMutate(url, payload, opts) {
    const body = Object.assign({}, payload, { sections: SECTIONS, sections_url: window.location.pathname });
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Cart request failed: ' + res.status);
    const data = await res.json();
    const drawer = drawerEl();
    if (!drawer || !data.sections) return data;

    if (typeof data.item_count === 'number') {
      drawer.classList.toggle('is-empty', data.item_count === 0);
      const items = drawer.querySelector('cart-drawer-items');
      if (items) items.classList.toggle('is-empty', data.item_count === 0);
    } else {
      drawer.classList.remove('is-empty');
    }

    const isOpen = drawer.classList.contains('active');
    if (opts && opts.silent && !isOpen) {
      applySections(data);
    } else {
      drawer.renderContents(data);
    }
    return data;
  }

  /* ---------------------- smart upsells (co-purchase) -------------------- */

  let recsData = null;
  let recsFetched = false;

  // Co-purchase data: worker KV first (refreshed monthly by a cron), then the
  // theme's static cart-recs.json snapshot as a fallback. The worker URL carries
  // today's date so the browser cache is busted at most once a day — new data
  // (and format changes like added sizes) always reach shoppers within 24h.
  const RECS_URL = TRACK_URL.replace('/cart-event', '/cart-recs');
  function todayKey() {
    try {
      return new Date().toISOString().slice(0, 10);
    } catch (e) {
      return '';
    }
  }

  async function getRecs(fallbackUrl) {
    if (recsFetched) return recsData;
    recsFetched = true;
    const tryFetch = async (u) => {
      if (!u) return null;
      try {
        const res = await fetch(u);
        if (res.ok) {
          const j = await res.json();
          if (j && j.recs && Object.keys(j.recs).length) return j;
        }
      } catch (e) {
        /* noop */
      }
      return null;
    };
    recsData = (await tryFetch(RECS_URL + '?d=' + todayKey())) || (await tryFetch(fallbackUrl));
    return recsData;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function imgWithWidth(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function upsellCard(rec) {
    // Products with more than one sellable variant get a size picker; the Add
    // button and price follow the chosen size.
    const variants = rec.variants && rec.variants.length ? rec.variants : [{ id: rec.variant, title: '', price: rec.price }];
    const multi = variants.length > 1;
    const def = variants[0];
    const compareHtml =
      !multi && rec.compare && rec.compare > rec.price ? '<s class="cart-upsells__compare">' + money(rec.compare) + '</s> ' : '';
    const sizeSel = multi
      ? '<select class="cart-upsells__size" aria-label="Choose size for ' +
        escapeHtml(rec.title) +
        '">' +
        variants
          .map(function (v) {
            return '<option value="' + v.id + '" data-price="' + v.price + '">' + escapeHtml(v.title) + '</option>';
          })
          .join('') +
        '</select>'
      : '';
    return (
      '<div class="cart-upsells__item">' +
      '<a class="cart-upsells__img" href="/products/' + escapeHtml(rec.handle) + '" tabindex="-1">' +
      (rec.image ? '<img src="' + escapeHtml(imgWithWidth(rec.image, 120)) + '" alt="" loading="lazy" width="48" height="48">' : '') +
      '</a>' +
      '<div class="cart-upsells__info">' +
      '<a class="cart-upsells__title" href="/products/' + escapeHtml(rec.handle) + '">' + escapeHtml(rec.title) + '</a>' +
      sizeSel +
      '<span class="cart-upsells__price">' + compareHtml + money(def.price) + '</span>' +
      '</div>' +
      '<button type="button" class="cart-upsells__add" data-variant="' + def.id + '" data-price="' + def.price + '" data-handle="' + escapeHtml(rec.handle) + '">Add +</button>' +
      '</div>'
    );
  }

  async function populateUpsells() {
    const box = document.getElementById('CartDrawer-Upsells');
    if (!box || box.dataset.populated) return;
    box.dataset.populated = 'loading';

    const cfg = config() || {};
    const cartIds = (box.dataset.cartProducts || '').split(',').filter(Boolean);
    const inCart = {};
    cartIds.forEach((id) => (inCart[id] = true));
    const limit = realItemCount() >= 2 ? 2 : 3;
    const picks = [];
    const picked = {};

    const excluded = (rec) =>
      inCart[String(rec.id)] ||
      picked[String(rec.id)] ||
      rec.handle === 'shipping-protection' ||
      (cfg.giftVariant && String(rec.variant) === String(cfg.giftVariant));

    // 1) Real co-purchase data from past Lawn Tips orders.
    const data = await getRecs(box.dataset.recsUrl);
    if (data && data.recs) {
      const scores = {};
      const byId = {};
      cartIds.forEach((pid) => {
        (data.recs[pid] || []).forEach((rec, idx) => {
          const key = String(rec.id);
          scores[key] = (scores[key] || 0) + (6 - idx);
          byId[key] = rec;
        });
      });
      Object.keys(scores)
        .sort((a, b) => scores[b] - scores[a])
        .forEach((key) => {
          const rec = byId[key];
          if (picks.length < limit && !excluded(rec)) {
            picks.push(rec);
            picked[key] = true;
          }
        });
    }

    // 2) Top up from Shopify's recommendations API if needed.
    if (picks.length < limit && box.dataset.sourceProduct) {
      try {
        const res = await fetch('/recommendations/products.json?product_id=' + box.dataset.sourceProduct + '&limit=8');
        const rd = await res.json();
        (rd.products || []).forEach((p) => {
          if (picks.length >= limit) return;
          const avail = (p.variants || []).filter((x) => x.available);
          const v = avail[0] || (p.variants || [])[0];
          if (!v || !p.available || p.type === 'ship_protect') return;
          // Storefront JSON prices are already GST-inclusive cents.
          const sizes = avail.map((x) => ({ id: x.id, title: x.title, price: x.price }));
          const rec = {
            id: p.id,
            variant: v.id,
            handle: p.handle,
            title: p.title,
            price: v.price,
            compare: v.compare_at_price || 0,
            image: p.featured_image || '',
          };
          if (sizes.length > 1) rec.variants = sizes;
          if (!excluded(rec)) {
            picks.push(rec);
            picked[String(rec.id)] = true;
          }
        });
      } catch (e) {
        /* noop */
      }
    }

    if (!picks.length) {
      box.dataset.populated = 'empty';
      return;
    }
    box.querySelector('.cart-upsells__list').innerHTML = picks.map(upsellCard).join('');
    box.hidden = false;
    box.dataset.populated = 'done';
  }

  /* --------------------- shippable-cart detection ------------------------ */

  // Whether the cart has at least one item that actually ships. Digital-only
  // carts (eBook, Lawn Plans, Lawn Calendar — all requires_shipping:false) have
  // nothing to ship, so shipping protection shouldn't be added or charged.
  // null = not resolved yet.
  let cartShippable = null;
  let shippableBusy = false;
  let lastShipCount = null;

  function applyNoShipAttr() {
    const d = drawerEl();
    if (d) d.toggleAttribute('data-lt-noship', cartShippable === false);
  }

  async function refreshShippable() {
    if (shippableBusy) return;
    shippableBusy = true;
    try {
      const c = await fetch('/cart.js', { headers: { accept: 'application/json' } }).then((r) => r.json());
      cartShippable = (c.items || []).some((i) => i.requires_shipping && i.product_type !== 'ship_protect');
    } catch (e) {
      cartShippable = true; // on a fetch hiccup, assume shippable — never strip protection on error
    }
    applyNoShipAttr();
    shippableBusy = false;
    syncState();
  }

  // Re-resolve only when the cart's item count changes (covers native Dawn
  // add/remove as well as our own mutations), so it isn't a fetch per re-render.
  function maybeRefreshShippable() {
    const count = cartTotals().count;
    if (count !== lastShipCount) {
      lastShipCount = count;
      refreshShippable();
    }
  }

  /* ----------------------- auto shipping protection ---------------------- */

  let protectBusy = false;
  let protectDisabled = false;

  function autoProtect() {
    const cfg = config();
    if (!cfg || protectBusy || protectDisabled) return;
    if (cfg.protectEnabled !== '1' || cfg.protectDefaultOn !== '1') return;
    if (cfg.protectKey) return; // already in the cart
    try {
      if (localStorage.getItem(PROTECT_OPTOUT_KEY)) return;
    } catch (e) {
      /* ignore */
    }
    if (!realItemCount()) return;
    // Only auto-add protection to carts that actually ship something. A
    // digital-only cart (eBook / Lawn Plan / Lawn Calendar) has nothing to
    // ship, so charging shipping protection would be unfair.
    if (cartShippable !== true) return;
    protectBusy = true;
    cartMutate('/cart/add.js', { items: [{ id: parseInt(cfg.protectVariant, 10), quantity: 1 }] }, { silent: true })
      .then(() => track('protect_on', { auto: 1 }))
      .catch(() => {
        protectDisabled = true; // don't loop on failure
      })
      .finally(() => {
        protectBusy = false;
      });
  }

  /* ------------------------------ free gift ------------------------------ */

  let giftBusy = false;
  let giftDisabled = false;

  function giftSync() {
    const cfg = config();
    if (!cfg || !cfg.giftVariant || giftBusy || giftDisabled) return;
    const threshold = parseInt(cfg.giftThreshold, 10) || 0;
    const qualifying = cartTotals().cents - (parseInt(cfg.giftLineCents, 10) || 0);
    const run = (url, payload) => {
      giftBusy = true;
      cartMutate(url, payload, { silent: true })
        .catch(() => {
          giftDisabled = true;
        })
        .finally(() => {
          giftBusy = false;
        });
    };
    if (!cfg.giftKey && qualifying >= threshold && realItemCount()) {
      run('/cart/add.js', { items: [{ id: parseInt(cfg.giftVariant, 10), quantity: 1, properties: { _lt_gift: '1' } }] });
    } else if (cfg.giftKey && qualifying < threshold) {
      run('/cart/change.js', { id: cfg.giftKey, quantity: 0 });
    }
  }

  /* ------------------------------- coupon -------------------------------- */

  // Restore the coupon UI after a re-render wiped it, with a result message.
  // Runs synchronously: the section swap has already happened by the time the
  // cart mutation resolves (rAF would stall in a backgrounded tab).
  function couponFeedback(code, text, isError) {
    const details = document.querySelector('#CartDrawer .cart-coupon');
    if (!details) return;
    details.open = true;
    const newMsg = details.querySelector('.cart-coupon__msg');
    const newInput = details.querySelector('.cart-coupon__input');
    if (newInput) newInput.value = isError ? code : '';
    if (newMsg) {
      newMsg.hidden = false;
      newMsg.textContent = text;
      newMsg.classList.toggle('cart-coupon__msg--error', !!isError);
      newMsg.classList.toggle('cart-coupon__msg--ok', !isError);
    }
  }

  async function applyCoupon() {
    const input = document.getElementById('CartDrawer-CouponInput');
    const msg = document.querySelector('.cart-coupon__msg');
    const code = input ? input.value.trim() : '';
    if (!code) return;
    if (msg) {
      msg.hidden = false;
      msg.textContent = 'Checking code…';
    }
    const before = cartTotals().cents;
    try {
      // Applying the code to the cart both validates it and recalculates totals.
      const data = await cartMutate('/cart/update.js', { discount: code });
      const entry = (data.discount_codes || []).find((c) => (c.code || '').toLowerCase() === code.toLowerCase());
      const applicable = entry ? entry.applicable === true : null;
      const after = typeof data.total_price === 'number' ? data.total_price : before;
      const saved = before - after;

      if (entry && !applicable) {
        // Not a real code (or not usable on this cart) — take it back off.
        await cartMutate('/cart/update.js', { discount: '' }, { silent: true });
        couponFeedback(code, '"' + code.toUpperCase() + '" isn’t a valid code, or can’t be used on this cart.', true);
        track('coupon', { code: code.slice(0, 40), handle: 'invalid' });
        return;
      }

      // Valid: also set the discount cookie as a belt-and-braces carry to checkout.
      fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual' }).catch(() => {});

      if (saved > 0) {
        couponFeedback(code, '✓ "' + code.toUpperCase() + '" applied — you’re saving ' + money(saved) + '!', false);
      } else if (applicable) {
        // Valid but no cart-total change (e.g. a free shipping code).
        couponFeedback(code, '✓ "' + code.toUpperCase() + '" applied — discount shows at checkout.', false);
      } else {
        // Older API shape without discount_codes — keep the code for checkout.
        couponFeedback(code, 'Code "' + code.toUpperCase() + '" saved — it will be applied at checkout.', false);
      }
      track('coupon', { code: code.slice(0, 40), handle: 'valid', cents: Math.max(0, saved) });
    } catch (e) {
      if (msg) {
        msg.hidden = false;
        msg.textContent = "Couldn't apply that code — please try again.";
      }
    }
  }

  /* ------------------------ orphaned protection -------------------------- */

  let removingOrphan = false;
  async function removeOrphanProtection() {
    const cfg = config();
    if (!cfg || removingOrphan) return;
    // Remove auto-added protection when there's nothing to protect: an empty
    // cart, or a cart with no shippable item (digital-only — eBook / Lawn Plan).
    if (cfg.protectKey && (!realItemCount() || cartShippable === false)) {
      removingOrphan = true;
      try {
        await cartMutate('/cart/change.js', { id: cfg.protectKey, quantity: 0 }, { silent: true });
      } catch (e) {
        /* noop */
      }
      removingOrphan = false;
    }
  }

  /* --------------------- free-shipping unlock celebration ---------------- */

  let lastUnlocked = null;
  let lastTotal = null;

  // Rain the Lawn Tips logo down the drawer (falls back to coloured bits if
  // no logo is configured).
  function burstCelebration() {
    const inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner || inner.querySelector('.cart-confetti')) return;
    const cfg = config() || {};
    const logo = cfg.logo || '';
    const wrap = document.createElement('div');
    wrap.className = 'cart-confetti' + (logo ? ' cart-confetti--logo' : '');
    const colors = ['#dc2c3b', '#f6c344', '#1a9c46', '#111925', '#f0813c'];
    for (let i = 0; i < 28; i++) {
      const bit = logo ? document.createElement('img') : document.createElement('i');
      if (logo) {
        bit.src = logo;
        bit.alt = '';
        const s = 18 + Math.random() * 16;
        bit.style.width = s + 'px';
      } else {
        bit.style.background = colors[i % colors.length];
        bit.style.width = 6 + Math.random() * 6 + 'px';
        bit.style.height = 8 + Math.random() * 8 + 'px';
      }
      bit.style.left = Math.random() * 100 + '%';
      bit.style.animationDelay = Math.random() * 0.35 + 's';
      bit.style.animationDuration = 1.1 + Math.random() * 1 + 's';
      wrap.appendChild(bit);
    }
    inner.appendChild(wrap);
    setTimeout(() => wrap.remove(), 2600);
  }

  // Nudge the free-shipping bar upward whenever the cart total grows, so the
  // eye is drawn to the progress it just made.
  function bumpBar() {
    const bar = document.querySelector('#CartDrawer .lt-shipbar');
    if (!bar) return;
    bar.classList.remove('lt-shipbar--bump');
    // Force reflow so re-adding the class restarts the animation.
    void bar.offsetWidth;
    bar.classList.add('lt-shipbar--bump');
  }

  // Visibly fill the free-shipping bar when the cart grows. On each render the
  // meter is a fresh element already sitting at its target width, so the CSS
  // width-transition never fires. Overlay a Web Animations API tween from the
  // previous percentage to the new one instead. Crucially this never writes
  // style.width: (a) writing it re-fires the drawer's MutationObserver →
  // syncState re-runs re-entrantly and reads our own stale write back as the
  // "target", parking the bar at a wrong percentage; (b) a WAAPI overlay always
  // releases back to the Liquid-rendered inline width, so even an interrupted
  // animation can never leave the bar resting at the wrong fill.
  let lastBarPct = null;
  function animateBarFill() {
    const bar = document.querySelector('#CartDrawer .lt-shipbar');
    const meter = bar && bar.querySelector('.lt-shipbar__meter');
    if (!bar || !meter) {
      lastBarPct = null;
      return;
    }
    // Percentage from the server-rendered cart totals — the same numbers Liquid
    // used for the inline width — never from the (animatable) style itself.
    const threshold = parseInt(bar.dataset.threshold, 10) || 0;
    const total = parseInt(bar.dataset.total, 10) || 0;
    if (!threshold) return;
    const target = Math.min((total * 100) / threshold, 100);
    const from = lastBarPct;
    lastBarPct = target;
    if (from === null || Math.abs(target - from) <= 0.5) return;
    try {
      meter.animate(
        [{ width: from + '%' }, { width: target + '%' }],
        { duration: 500, easing: 'cubic-bezier(0.25, 1, 0.4, 1)' }
      );
    } catch (e) {
      /* very old browsers: skip the tween, bar still shows the correct width */
    }
  }

  function checkCelebration() {
    const bar = document.querySelector('#CartDrawer .lt-shipbar');
    const cfg = config();
    const d = drawerEl();
    if (!bar) return;
    const total = parseInt(bar.dataset.total, 10) || 0;
    const count = parseInt(bar.dataset.count, 10) || 0;
    const unlocked = bar.dataset.unlocked === '1';
    const open = d && d.classList.contains('active');

    if (open && lastTotal !== null && total > lastTotal) bumpBar();
    if (open && unlocked && lastUnlocked === false) {
      // The cart just crossed the free-shipping line while the drawer was open —
      // i.e. the shopper added more to qualify. Log it (independent of the
      // confetti setting) so the dashboard can show the bar's nudge effect.
      track('unlock', { cents: total, count: count });
      if (cfg && cfg.confetti === '1') burstCelebration();
    }
    lastTotal = total;
    lastUnlocked = unlocked;
  }

  /* ---------------------------- event wiring ----------------------------- */

  document.addEventListener('click', (e) => {
    const upsellAdd = e.target.closest('.cart-upsells__add');
    if (upsellAdd) {
      upsellAdd.disabled = true;
      upsellAdd.textContent = 'Adding…';
      track('upsell_add', {
        handle: upsellAdd.dataset.handle,
        cents: parseInt(upsellAdd.dataset.price, 10) || 0,
      });
      cartMutate('/cart/add.js', { items: [{ id: parseInt(upsellAdd.dataset.variant, 10), quantity: 1 }] }).catch(() => {
        upsellAdd.disabled = false;
        upsellAdd.textContent = 'Add +';
      });
      return;
    }

    const protectToggle = e.target.closest('.cart-protect__toggle');
    if (protectToggle) {
      const row = protectToggle.closest('.cart-protect');
      const isOn = protectToggle.classList.contains('is-on');
      protectToggle.disabled = true;
      if (isOn) {
        try {
          localStorage.setItem(PROTECT_OPTOUT_KEY, '1');
        } catch (err) {
          /* ignore */
        }
        track('protect_off');
        cartMutate('/cart/change.js', { id: row.dataset.itemKey, quantity: 0 }).catch(() => {
          protectToggle.disabled = false;
        });
      } else {
        try {
          localStorage.removeItem(PROTECT_OPTOUT_KEY);
        } catch (err) {
          /* ignore */
        }
        track('protect_on');
        cartMutate('/cart/add.js', { items: [{ id: parseInt(row.dataset.variant, 10), quantity: 1 }] }).catch(() => {
          protectToggle.disabled = false;
        });
      }
      return;
    }

    if (e.target.closest('#CartDrawer-CouponApply')) {
      applyCoupon();
      return;
    }

    const discRemove = e.target.closest('.cart-discount-remove');
    if (discRemove) {
      discRemove.disabled = true;
      // Clear the cart discount and the legacy discount cookie so it doesn't
      // linger to checkout.
      try {
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      } catch (err) {
        /* ignore */
      }
      cartMutate('/cart/update.js', { discount: '' }).catch(() => {
        discRemove.disabled = false;
      });
      return;
    }

    if (e.target.closest('#CartDrawer-Checkout')) {
      const t = cartTotals();
      const cfg = config() || {};
      track('checkout', { cents: t.cents, count: t.count, handle: cfg.protectKey ? 'with-protection' : 'no-protection' });
    }
  });

  // Size picker on an upsell: point the Add button at the chosen variant and
  // update the shown price. Runs in the CAPTURE phase and stops the event, so it
  // never reaches Dawn's <cart-drawer-items> quantity validator — which would
  // otherwise treat this <select> as a cart quantity input and throw (showing a
  // bogus "increments of undefined" error).
  document.addEventListener(
    'change',
    (e) => {
      const sel = e.target.closest && e.target.closest('.cart-upsells__size');
      if (!sel) return;
      e.stopPropagation();
      const opt = sel.options[sel.selectedIndex];
      const item = sel.closest('.cart-upsells__item');
      if (!item || !opt) return;
      const btn = item.querySelector('.cart-upsells__add');
      const priceEl = item.querySelector('.cart-upsells__price');
      const price = parseInt(opt.dataset.price, 10) || 0;
      if (btn) {
        btn.dataset.variant = opt.value;
        btn.dataset.price = price;
      }
      if (priceEl) priceEl.textContent = money(price);
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.id === 'CartDrawer-CouponInput') {
      e.preventDefault();
      applyCoupon();
    }
  });

  /* -------------------- drawer open + re-render watcher ------------------ */

  let openTracked = false;

  function syncState() {
    maybeRefreshShippable();
    autoProtect();
    giftSync();
    removeOrphanProtection();
    animateBarFill();
    checkCelebration();
    syncWholesaleTotal();
    applyNoShipAttr();
  }

  function onDrawerMutation() {
    const drawer = drawerEl();
    if (!drawer) return;
    const isOpen = drawer.classList.contains('active');
    if (isOpen && !openTracked) {
      openTracked = true;
      const t = cartTotals();
      track('open', { cents: t.cents, count: t.count });
    }
    if (!isOpen && openTracked) openTracked = false;
    if (isOpen) populateUpsells();
    syncState();
  }

  function init() {
    const drawer = drawerEl();
    if (!drawer) return;
    // One-time injected styles:
    //  (1) hide the shipping-protection row on digital-only carts — the
    //      data-lt-noship attribute lives on <cart-drawer> (not replaced on
    //      re-render), so the row stays hidden across section swaps, no flash;
    //  (2) constantly pulse the free-shipping progress line so shoppers notice
    //      how close they are — gated on data-unlocked="0" so it stops once
    //      free shipping is reached (the bar turns green). Glow sits on the rail
    //      (its own box-shadow isn't clipped by the rail's overflow:hidden; a
    //      glow on the meter would be). Both respect prefers-reduced-motion.
    if (!document.getElementById('lt-cart-style')) {
      const st = document.createElement('style');
      st.id = 'lt-cart-style';
      st.textContent =
        'cart-drawer[data-lt-noship] #CartDrawer-Protect{display:none}' +
        '.lt-shipbar[data-unlocked="0"] .lt-shipbar__meter{animation:lt-bar-pulse 1.5s ease-in-out infinite}' +
        '.lt-shipbar[data-unlocked="0"] .lt-shipbar__rail{animation:lt-bar-glow 1.5s ease-in-out infinite}' +
        '@keyframes lt-bar-pulse{0%,100%{filter:brightness(1)}50%{filter:brightness(1.28) saturate(1.1)}}' +
        '@keyframes lt-bar-glow{0%,100%{box-shadow:0 0 0 0 rgba(220,44,59,0)}50%{box-shadow:0 0 9px 2px rgba(220,44,59,.5)}}' +
        '@media (prefers-reduced-motion: reduce){' +
        '.lt-shipbar[data-unlocked="0"] .lt-shipbar__meter,' +
        '.lt-shipbar[data-unlocked="0"] .lt-shipbar__rail{animation:none}}';
      document.head.appendChild(st);
    }
    const bar = document.querySelector('#CartDrawer .lt-shipbar');
    lastUnlocked = bar ? bar.dataset.unlocked === '1' : null;
    lastTotal = bar ? parseInt(bar.dataset.total, 10) || 0 : null;
    new MutationObserver(onDrawerMutation).observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    // Wholesale only: WPD repaints the per-line prices asynchronously after load
    // and after each re-render, so make a few timed passes to pick them up. No
    // dedicated observer here — the main drawer observer already calls
    // syncState() → syncWholesaleTotal(), and its writes are equality-guarded so
    // the sequence converges instead of looping.
    const cfg0 = config();
    if (cfg0 && cfg0.wholesale === '1') {
      [400, 1200, 2500, 5000].forEach((d) => setTimeout(syncWholesaleTotal, d));
    }
    syncState();
    // The old /cart page redirects here with ?cart=open — open the drawer.
    try {
      if (new URLSearchParams(location.search).get('cart') === 'open') {
        history.replaceState({}, document.title, location.pathname);
        setTimeout(() => {
          const d = drawerEl();
          if (d && typeof d.open === 'function') d.open();
        }, 400);
      }
    } catch (e) {
      /* noop */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
